import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Plus, FilePdf, Trash, MagnifyingGlass, Buildings, CircleNotch, CheckCircle, Warning } from "@phosphor-icons/react";
import { useBrandedPdf } from "./useBrandedPdf";
import { fmtDate } from "../../lib/dateUtil";
import HseNewDocumentDialog from "./HseNewDocumentDialog";
import QuizInsightsCard from "./QuizInsightsCard";
import HseProfileDialog from "./HseProfileDialog";

export const HSE_STATUS_CLASS = { draft: "bg-slate-100 text-slate-700", approved: "bg-emerald-100 text-emerald-700", superseded: "bg-red-100 text-red-700", archived: "bg-amber-100 text-amber-800" };
const REVIEW_CLASS = { overdue: "bg-red-100 text-red-700", due: "bg-amber-100 text-amber-800" };

function Coverage({ reg, onPick }) {
  if (!reg) return null;
  const pct = Math.round((reg.essentials_approved / Math.max(1, reg.essentials_total)) * 100);
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid="hse-coverage">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Compliance coverage</p>
          <p className="text-sm"><span className="text-2xl font-[Chivo] font-bold">{reg.essentials_approved}</span> <span className="text-muted-foreground">of {reg.essentials_total} essential documents approved</span></p>
        </div>
        <div className="text-right text-[11px] text-muted-foreground">
          {reg.totals.approved} approved · {reg.totals.drafts} drafts{reg.totals.generating ? ` · ${reg.totals.generating} being written` : ""}
          {reg.totals.review_overdue ? <span className="text-red-700 font-semibold"> · {reg.totals.review_overdue} review overdue</span> : ""}
        </div>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {reg.essentials.map(e => (
          <button key={e.type_key} type="button" onClick={() => onPick(e)} data-testid={`hse-essential-${e.type_key}`}
            className={`text-[11px] px-2 py-1 rounded-md border flex items-center gap-1 transition-colors ${e.approved ? (e.review_state === "overdue" ? "border-red-300 bg-red-50 text-red-800" : "border-emerald-300 bg-emerald-50 text-emerald-800") : e.draft ? "border-slate-300 bg-slate-50 text-slate-700" : "border-dashed border-border text-muted-foreground hover:border-primary/60"}`}>
            {e.approved ? <CheckCircle size={12} weight="fill" /> : e.draft ? <CircleNotch size={12} /> : <Plus size={12} />}{e.label}{e.review_state === "overdue" && <Warning size={12} weight="fill" />}
          </button>
        ))}
      </div>
    </div>
  );
}

/** HSE document library: compliance coverage, filters, list, create (AI) and quick PDF. */
export default function HseDocumentsPanel() {
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [reg, setReg] = useState(null);
  const [rows, setRows] = useState([]);
  const [group, setGroup] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [preset, setPreset] = useState("");
  const [showProfile, setShowProfile] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadMeta = useCallback(() => {
    API.get("/hse/catalogue").then(({ data }) => setCat(data)).catch(() => {});
    API.get("/hse/register").then(({ data }) => setReg(data)).catch(() => {});
  }, []);
  const load = useCallback(() => {
    setLoading(true);
    API.get("/hse/documents", { params: { group: group || undefined, status: status || undefined, search: search || undefined } })
      .then(({ data }) => setRows(data.documents || [])).catch(() => {}).finally(() => setLoading(false));
  }, [group, status, search]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    if (!rows.some(r => r.ai?.status === "pending")) return undefined;
    const t = setInterval(() => { load(); loadMeta(); }, 6000);
    return () => clearInterval(t);
  }, [rows, load, loadMeta]);

  const openNew = (typeKey = "") => {
    if (cat && !cat.profile_complete) { setShowProfile(true); toast.message("Set up your company H&S profile first — it takes two minutes and every document is written from it."); return; }
    setPreset(typeKey); setShowNew(true);
  };
  const pickEssential = (e) => (e.doc_id ? navigate(`/health-safety/docs/${e.doc_id}`) : openNew(e.type_key));
  const remove = async (r) => {
    if (!window.confirm(`Delete draft ${r.reference} v${r.version}?`)) return;
    try { await API.delete(`/hse/documents/${r.id}`); toast.success("Deleted"); load(); loadMeta(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const { openBranded, brandingDialog } = useBrandedPdf();
  const pdf = (r) => openBranded({ url: `/hse/documents/${r.id}/pdf`, filename: `${r.reference}_v${r.version}.pdf`, title: `${r.reference} PDF branding` });

  return (
    <div className="space-y-3" data-testid="hse-documents-panel">
      <Coverage reg={reg} onPick={pickEssential} />
      <QuizInsightsCard />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title or reference…" className="h-8 pl-8 text-sm" data-testid="hse-docs-search" />
        </div>
        <select value={group} onChange={e => setGroup(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="hse-docs-group">
          <option value="">All groups</option>
          {(cat?.groups || []).map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="hse-docs-status">
          <option value="">Live & drafts</option>
          <option value="approved">Approved</option>
          <option value="draft">Drafts</option>
          <option value="superseded">Superseded</option>
          <option value="archived">Withdrawn</option>
        </select>
        <Button size="sm" variant="outline" onClick={() => setShowProfile(true)} data-testid="hse-docs-profile-btn"><Buildings size={14} className="mr-1" /> Company profile{cat && !cat.profile_complete && <span className="ml-1 h-2 w-2 rounded-full bg-amber-500" />}</Button>
        <Button size="sm" onClick={() => openNew()} data-testid="hse-docs-new-btn"><Plus size={14} className="mr-1" /> New document</Button>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-3 py-2">Document</th><th className="text-left px-3 py-2 hidden md:table-cell">Type</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2 hidden lg:table-cell">Approved</th><th className="text-left px-3 py-2 hidden lg:table-cell">Review</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/health-safety/docs/${r.id}`)} data-testid={`hse-doc-row-${r.id}`}>
                <td className="px-3 py-2"><div className="font-medium leading-tight">{r.title}</div><div className="text-[11px] text-muted-foreground">{r.reference} · v{r.version}</div></td>
                <td className="px-3 py-2 hidden md:table-cell text-xs text-muted-foreground">{r.type_label}</td>
                <td className="px-3 py-2">
                  <Badge className={`text-[10px] ${HSE_STATUS_CLASS[r.status] || ""}`} data-testid={`hse-doc-status-${r.id}`}>{r.status === "archived" ? "withdrawn" : r.status}</Badge>
                  {r.acks && <Badge className={`text-[10px] ${r.acks.acknowledged >= r.acks.total && r.acks.total > 0 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} title="Operatives who have read and signed this version" data-testid={`hse-doc-acks-${r.id}`}>read {r.acks.acknowledged}/{r.acks.total}</Badge>}
                  {r.ai?.status === "pending" && <Badge className="ml-1 text-[10px] bg-sky-100 text-sky-700 animate-pulse" data-testid={`hse-doc-generating-${r.id}`}>AI writing…</Badge>}
                  {r.ai?.status === "failed" && <Badge className="ml-1 text-[10px] bg-red-100 text-red-700">AI failed</Badge>}
                </td>
                <td className="px-3 py-2 hidden lg:table-cell text-xs">{r.approved_at ? <>{r.approved_by_name}<div className="text-[11px] text-muted-foreground">{fmtDate(r.approved_at)}</div></> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3 py-2 hidden lg:table-cell text-xs">{r.review_date ? <span className="flex items-center gap-1">{fmtDate(r.review_date)}{REVIEW_CLASS[r.review_state] && <Badge className={`text-[10px] ${REVIEW_CLASS[r.review_state]}`}>{r.review_state}</Badge>}</span> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => pdf(r)} title="PDF" data-testid={`hse-doc-pdf-${r.id}`}><FilePdf size={15} /></Button>
                  {r.status === "draft" && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(r)} title="Delete draft" data-testid={`hse-doc-delete-${r.id}`}><Trash size={15} /></Button>}
                </td>
              </tr>
            ))}
            {!rows.length && !loading && <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-muted-foreground" data-testid="hse-docs-empty">No documents yet. Start with your Health &amp; Safety Policy — the AI drafts it in about a minute.</td></tr>}
          </tbody>
        </table>
      </div>

      <HseNewDocumentDialog open={showNew} onOpenChange={setShowNew} catalogue={cat} presetType={preset} />
      <HseProfileDialog open={showProfile} onOpenChange={setShowProfile} onSaved={loadMeta} />
      {brandingDialog}
    </div>
  );
}
